import { useState } from "react";
import { AlertCircle, ArrowRight, Check, Loader2, ShieldCheck, Sparkles } from "lucide-react";
import { t } from "@/lib/i18n";
import { Hint, SideCard, Steps, SuccessBox, monoInputCls, type PanelProps } from "./channel-wizard-kit";

/* Viber Business: официальный аккаунт по токену из кабинета Viber Business.
   Проверка токена → данные аккаунта → подключение. */

const DEMO_TOKEN = "5a1b7c94e2f84d0c9b3a6e71f0d4c8b29e3a5f6d";
const TOKEN_RE = /^[A-Za-z0-9]{24,}$/;

export default function ViberBotPanel({ onDone }: PanelProps) {
  const [token, setToken] = useState("");
  const [check, setCheck] = useState<"idle" | "checking" | "ok" | "error">("idle");

  const verify = () => {
    setCheck("checking");
    setTimeout(() => setCheck(TOKEN_RE.test(token.trim()) ? "ok" : "error"), 800);
  };

  const connect = () => {
    onDone("Viber Business · Hub Market", [
      { label: t("Тип"), value: "Viber Business" },
      { label: t("Аккаунт"), value: "Hub Market" },
      { label: t("Отправитель"), value: "Hub Market" },
      { label: t("Рассылки"), value: t("доступны") },
      { label: t("Токен"), value: t("сохранён в защищённом виде") },
    ]);
  };

  return (
    <div className="grid flex-1 overflow-y-auto lg:grid-cols-[minmax(0,1fr)_20rem]">
      <div className="min-w-0 p-4 md:p-5">
        <Steps
          active={check === "ok" ? 3 : token.trim().length > 0 ? 2 : 1}
          items={[
            t("Откройте кабинет Viber Business и создайте бота"),
            t("Скопируйте токен доступа и имя отправителя"),
            t("Вставьте токен и проверьте соединение"),
          ]}
        />

        <div className="mt-4">
          <label
            htmlFor="viber-token"
            className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
          >
            {t("Токен доступа")}
          </label>
          <div className="mt-1.5 flex flex-col gap-2 sm:flex-row">
            <input
              id="viber-token"
              value={token}
              onChange={(e) => {
                setToken(e.target.value);
                setCheck("idle");
              }}
              placeholder="5a1b7c94e2f84d0c9b3a6e71f0d4c8b2"
              autoComplete="off"
              spellCheck={false}
              className={monoInputCls}
            />
            <button
              type="button"
              onClick={verify}
              disabled={token.trim().length === 0 || check === "checking"}
              className="inline-flex h-10 flex-none cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3.5 text-[12.5px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {check === "checking" ? <Loader2 className="size-3.5 animate-spin" aria-hidden /> : null}
              {check === "checking" ? t("Проверяем…") : t("Проверить токен")}
            </button>
          </div>

          <button
            type="button"
            onClick={() => {
              setToken(DEMO_TOKEN);
              setCheck("idle");
            }}
            className="mt-2 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-[11px] font-semibold text-primary transition hover:brightness-110"
          >
            <Sparkles className="size-3" aria-hidden />
            {t("Заполнить демо-токеном")}
          </button>

          {check === "error" ? (
            <p className="mt-2.5 flex items-start gap-1.5 rounded-btn border border-red-400/40 bg-red-500/10 px-2.5 py-2 text-[11.5px] leading-relaxed text-red-600">
              <AlertCircle className="mt-0.5 size-3.5 flex-none" aria-hidden />
              {t("Токен не подходит. Нужна длинная строка из букв и цифр без пробелов — её выдают в кабинете Viber Business.")}
            </p>
          ) : null}

          {check === "ok" ? (
            <SuccessBox title={t("Токен рабочий — аккаунт найден")}>
              <div className="flex flex-col">
                {[
                  [t("Аккаунт"), "Hub Market"],
                  [t("Идентификатор"), "pa:hub-market-8f31"],
                  [t("Вебхук"), t("установлен")],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="flex items-center justify-between gap-3 border-b border-emerald-600/20 py-1.5 last:border-0"
                  >
                    <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</span>
                    <span className="text-[12.5px] font-semibold">{value}</span>
                  </div>
                ))}
              </div>
            </SuccessBox>
          ) : null}

          <button
            type="button"
            onClick={connect}
            disabled={check !== "ok"}
            className="mt-3 inline-flex h-10 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-btn bg-primary px-4 text-[12.5px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("Подключить аккаунт")}
            <ArrowRight className="size-3.5" aria-hidden />
          </button>
        </div>

        <Hint icon={<ShieldCheck className="size-3.5" />}>
          {t("Токен видит только администратор. Продавцы получают диалоги, а не доступы.")}
        </Hint>
      </div>

      <div className="flex min-w-0 flex-col gap-3 border-t border-border bg-background p-4 lg:border-s lg:border-t-0 md:p-5">
        <SideCard title={t("Превью аккаунта")}>
          <div className="mt-2.5 flex items-center gap-2.5">
            <span
              className="grid size-10 flex-none place-items-center rounded-full bg-primary/10 text-lg leading-none text-primary"
              aria-hidden
            >
              <i className="ri-phone-line" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-[12.5px] font-semibold">Hub Market</p>
              <p className="truncate text-[11px] text-muted-foreground">
                {check === "ok" ? t("проверен · официальный") : t("ждём проверку токена")}
              </p>
            </div>
          </div>
        </SideCard>

        <SideCard title={t("Что даёт официальный аккаунт")}>
          <ul className="mt-2 flex flex-col gap-1.5">
            {[
              t("Рассылки по согласованным шаблонам"),
              t("Имя бренда и аватар в диалоге"),
              t("Автоответы и сценарии"),
            ].map((item) => (
              <li key={item} className="flex items-start gap-1.5 text-[11px] leading-relaxed text-muted-foreground">
                <Check className="mt-0.5 size-3 flex-none text-primary" strokeWidth={3} aria-hidden />
                {item}
              </li>
            ))}
          </ul>
        </SideCard>
      </div>
    </div>
  );
}